import { CommonModule } from "@angular/common";
import { inject, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { EditColComponent } from "./edit-col.component";
import { InfoComponent } from "./info.component";

import { AgGridModule } from "@ag-grid-community/angular";

import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { ModuleRegistry } from "@ag-grid-community/core";
import { InfiniteRowModelModule } from "@ag-grid-community/infinite-row-model";
import { MasterDetailModule } from "@ag-grid-enterprise/master-detail";
import { MenuModule as AgMenuModule } from "@ag-grid-enterprise/menu";
import { RangeSelectionModule } from "@ag-grid-enterprise/range-selection";
import { RichSelectModule } from "@ag-grid-enterprise/rich-select";
import { RowGroupingModule } from "@ag-grid-enterprise/row-grouping";
// Needed by AnalysisGridDialogComponent: without the side bar and its two tool panels the
// Analyze dialog renders a plain grid with no column chooser, no pivot and no filter panel.
import { SideBarModule } from "@ag-grid-enterprise/side-bar";
import { ColumnsToolPanelModule } from "@ag-grid-enterprise/column-tool-panel";
import { FiltersToolPanelModule } from "@ag-grid-enterprise/filter-tool-panel";
import { ExcelExportModule } from "@ag-grid-enterprise/excel-export";
import { ServerSideRowModelModule } from "@ag-grid-enterprise/server-side-row-model";
import { SetFilterModule } from "@ag-grid-enterprise/set-filter";

import { NgxCurrencyDirective } from "ngx-currency";

import { TemplateRendererComponent } from "./template-renderer.component";

import { DragDropModule } from "@angular/cdk/drag-drop";

import { AddressComponent } from "./address.component";
import { AgLoadingOverlayComponent } from "./ag-loading-overlay.component";

import { BackButtonComponent } from "./back-button.component";

import { EditPropComponent } from "./edit-prop.component";

import { ValidationSaveErrorsDialogComponent } from "./validation-save-errors-dialog.component";

import { BreezeValidator } from "./breeze-validator";
import { EditNotesDialogComponent } from "./edit-notes-dialog.component";
import { FnValidator } from "./fn-validator";
import { OneFieldDialogComponent } from "./one-field-dialog.component";
import { EmailPromptDialogComponent } from "./email-prompt-dialog.component";

import { AgChartsModule } from "ag-charts-angular";
import { BlockUIModule } from "primeng/blockui";
import { DialogService, DynamicDialogModule } from "primeng/dynamicdialog";
import { AddonFinderDialogComponent } from "./addon-finder-dialog.component";
import { AddressDialogComponent } from "./address-dialog.component";
import { AgGridWrapperComponent } from "./ag-grid-wrapper.component";
import { AuthUserSelectDialogComponent } from "./auth-user-select-dialog.component";
import { ButtonRendererComponent } from "./button-renderer.component";
import { MenuRendererComponent } from "./menu-renderer.component";
import { DynamicChildLoaderDirective } from "./dynamic-child-loader.directive";
import { FileSelectDialogComponent } from "./file-select-dialog.component";
import { FileViewDialogComponent } from "./file-view-dialog.component";
import { ImageModule as UniImageModule } from "./image/image.module";

import { UniDialogComponent } from "./uni-dialog.component";
import { UniPageComponent } from "./uni-page.component";
import { BreadcrumbComponent } from "./breadcrumb.component";
// import { PdfComponent } from './pdf-dialog.component';

import { BreezeValidatorDirective } from "./breeze-validator.directive";
import { UniFieldComponent } from "./uni-field.component";
import { QbFieldComponent } from "./qb-field.component";

import { AccordionModule } from "primeng/accordion";
import { AutoCompleteModule } from "primeng/autocomplete";
import { AvatarModule } from "primeng/avatar";
import { AvatarGroupModule } from "primeng/avatargroup";
import { BadgeModule } from "primeng/badge";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { ButtonModule } from "primeng/button";
import { CalendarModule } from "primeng/calendar";
import { CardModule } from "primeng/card";
import { CarouselModule } from "primeng/carousel";
import { CascadeSelectModule } from "primeng/cascadeselect";
import { CheckboxModule } from "primeng/checkbox";
import { ChipModule } from "primeng/chip";
import { ColorPickerModule } from "primeng/colorpicker";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { ConfirmPopupModule } from "primeng/confirmpopup";
import { ContextMenuModule } from "primeng/contextmenu";
import { DataViewModule } from "primeng/dataview";
import { DialogModule } from "primeng/dialog";
import { DividerModule } from "primeng/divider";
import { DropdownModule } from "primeng/dropdown";
import { FieldsetModule } from "primeng/fieldset";
import { FileUploadModule } from "primeng/fileupload";
import { FloatLabelModule } from "primeng/floatlabel";
import { AutoFocusModule } from "primeng/autofocus";
// import {FullCalendarModule} from 'primeng/fullcalendar';
import { GalleriaModule } from "primeng/galleria";
import { IconFieldModule } from "primeng/iconfield";
import { ImageModule } from "primeng/image";
import { InplaceModule } from "primeng/inplace";
import { InputIconModule } from "primeng/inputicon";
import { InputMaskModule } from "primeng/inputmask";
import { InputNumberModule } from "primeng/inputnumber";
import { InputSwitchModule } from "primeng/inputswitch";
import { InputTextModule } from "primeng/inputtext";
import { KnobModule } from "primeng/knob";
import { TextareaModule } from "primeng/textarea";
// import { LightboxModule } from 'primeng/lightbox';
import { RouterModule } from "@angular/router";
import { ConfirmationService, MessageService } from "primeng/api";
import { ListboxModule } from "primeng/listbox";
import { MegaMenuModule } from "primeng/megamenu";
import { MenuModule } from "primeng/menu";
import { MenubarModule } from "primeng/menubar";
import { MessageModule } from "primeng/message";
import { MessagesModule } from "primeng/messages";
import { MultiSelectModule } from "primeng/multiselect";
import { OrderListModule } from "primeng/orderlist";
import { OrganizationChartModule } from "primeng/organizationchart";
import { OverlayPanelModule } from "primeng/overlaypanel";
import { PaginatorModule } from "primeng/paginator";
import { PanelModule } from "primeng/panel";
import { PanelMenuModule } from "primeng/panelmenu";
import { PasswordModule } from "primeng/password";
import { PickListModule } from "primeng/picklist";
import { ProgressBarModule } from "primeng/progressbar";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { RadioButtonModule } from "primeng/radiobutton";
import { RatingModule } from "primeng/rating";
import { RippleModule } from "primeng/ripple";
import { ScrollerModule } from "primeng/scroller";
import { ScrollPanelModule } from "primeng/scrollpanel";
import { ScrollTopModule } from "primeng/scrolltop";
import { SelectButtonModule } from "primeng/selectbutton";
import { Select, SelectModule } from "primeng/select";
import { DrawerModule } from "primeng/drawer";
import { SidebarModule } from "primeng/sidebar";
import { SkeletonModule } from "primeng/skeleton";
import { SliderModule } from "primeng/slider";
import { SplitButtonModule } from "primeng/splitbutton";
import { SplitterModule } from "primeng/splitter";
import { StepsModule } from "primeng/steps";
import { TableModule } from "primeng/table";
import { TabMenuModule } from "primeng/tabmenu";
import { TabsModule } from "primeng/tabs";
import { TabViewModule } from "primeng/tabview";
import { TagModule } from "primeng/tag";
import { TerminalModule } from "primeng/terminal";
import { TieredMenuModule } from "primeng/tieredmenu";
import { TimelineModule } from "primeng/timeline";
import { ToastModule } from "primeng/toast";
import { ToggleButtonModule } from "primeng/togglebutton";
import { ToolbarModule } from "primeng/toolbar";
import { TooltipModule } from "primeng/tooltip";
import { TreeTableModule } from "primeng/treetable";
import { EditGenericEntityDialogComponent } from "./edit-generic-entity-dialog.component";
import { UniNavCardComponent } from "./uni-nav-card.component";
import { EntityStateRendererComponent } from "./entity-state-renderer.component";
import { DatePickerModule } from "primeng/datepicker";
import { HttpClient } from "@angular/common/http";
import { ModelLibraryBackingStoreAdapter } from "breeze-client/adapter-model-library-backing-store";
import { UriBuilderJsonAdapter } from "breeze-client/adapter-uri-builder-json";
import { AjaxHttpClientAdapter } from "breeze-client/adapter-ajax-httpclient";
import { DataServiceWebApiAdapter } from "breeze-client/adapter-data-service-webapi";
import { UniformaxProvider } from "./entity-manager-provider";
import { HtmlEditorComponent } from "./editor/html-editor.component";
import { KeyMenuDirective } from "./editor/key-menu.directive";
import { TiptapEditorDirective } from "ngx-tiptap";
import { ImageInsertDialogComponent } from "./editor/image-insert-dialog.component";
import { GridToolTipValueDialog } from "./grid-tooltip-value.dialog";
import { InfoDialogComponent } from "./info-dialog.component";
import { YesNoDialogComponent } from "./yes-no-dialog.component";
import { InvoiceDialogComponent } from "./invoice-dialog.component";
import { ItemDetailLogDialogComponent } from "./item-detail-log-dialog.component";
import { ItemDetailLogGridComponent } from "./item-detail-log-grid.component";
import { ItemStoryComponent } from "./item-story.component";
import { ScannerInputComponent } from "./scanner-input.component";
import { ProductExposurePanelComponent } from "./product-exposure-panel.component";
import { QbHealthBannerComponent } from "./qb-health-banner.component";
import { DatepickerAmPmPatchDirective } from "./datepicker-patched.directive";

const stdModules = [
  CommonModule,
  FormsModule,
  ReactiveFormsModule,
  RouterModule,
  NgxCurrencyDirective,

  DragDropModule,
  AgChartsModule,
  UniImageModule,

  //PrimeNG modules
  DynamicDialogModule,
  ButtonModule,
];

const PRIMENG_MODULES: unknown[] = [
  AccordionModule,
  AutoCompleteModule,
  AutoFocusModule,
  AvatarModule,
  AvatarGroupModule,
  BadgeModule,
  BreadcrumbModule,
  ButtonModule,
  BlockUIModule,
  CalendarModule,
  CardModule,
  CarouselModule,
  CascadeSelectModule,
  CheckboxModule,
  ChipModule,
  ConfirmDialogModule,
  ConfirmPopupModule,
  ColorPickerModule,
  ContextMenuModule,
  DataViewModule,
  DialogModule,
  DividerModule,
  DropdownModule,
  FieldsetModule,
  FileUploadModule,
  FloatLabelModule,
  // FullCalendarModule, <- @fullcalendar not Angular 13 ready
  GalleriaModule,
  IconFieldModule,
  ImageModule,
  InplaceModule,
  InputIconModule,
  InputNumberModule,
  InputMaskModule,
  InputSwitchModule,
  InputTextModule,
  TextareaModule,
  KnobModule,
  // LightboxModule,
  ListboxModule,
  MegaMenuModule,
  MenuModule,
  MenubarModule,
  MessageModule,
  MessagesModule,
  MultiSelectModule,
  OrderListModule,
  OrganizationChartModule,
  OverlayPanelModule,
  PaginatorModule,
  PanelModule,
  PanelMenuModule,
  PasswordModule,
  PickListModule,
  ProgressBarModule,
  ProgressSpinnerModule,
  RadioButtonModule,
  RatingModule,
  RippleModule,
  ScrollPanelModule,
  ScrollTopModule,
  SelectButtonModule,
  SelectModule,
  DrawerModule,
  SidebarModule,
  SkeletonModule,
  SliderModule,
  SplitButtonModule,
  SplitterModule,
  StepsModule,
  TableModule,
  TabMenuModule,
  TabsModule,
  TabViewModule,
  TagModule,
  TerminalModule,
  TimelineModule,
  TieredMenuModule,
  ToastModule,
  ToggleButtonModule,
  ToolbarModule,
  TooltipModule,
  //TreeModule, <= development time error re: @ciclon/angular-tree-component
  TreeTableModule,
  ScrollerModule,
  DatePickerModule,
];

import { RepointColorDialogComponent } from './repoint-color-dialog.component';
import { SizeMappingDialogComponent } from './size-mapping-dialog.component';
import { AnalysisGridDialogComponent } from './analysis-grid-dialog.component';

@NgModule({
  imports: [
    ...stdModules,
    PRIMENG_MODULES,
    AgGridModule,
    TiptapEditorDirective,

    // No longer needed after Ag30
    // AgGridModule.withComponents([
    //   TemplateRendererComponent,
    //   AgCheckboxCellComponent,
    //   AgMatDatepickerComponent,
    //   AgLoadingOverlayComponent,
    // ]),
  ],
  exports: [
    ...stdModules,
    PRIMENG_MODULES,
    AgGridModule,
    EditColComponent,
    InfoComponent,
    AddressComponent,
    AddressDialogComponent,
    AgGridWrapperComponent,
    BreezeValidatorDirective,
    UniPageComponent,
    UniFieldComponent,
    QbFieldComponent,
    UniDialogComponent,
    AuthUserSelectDialogComponent,
    EditGenericEntityDialogComponent,
    // PdfComponent,
    HtmlEditorComponent,
    ImageInsertDialogComponent,
    EditNotesDialogComponent,
    // BaseDialogComponent,
    EditPropComponent,
    BackButtonComponent,
    ValidationSaveErrorsDialogComponent,
    BreezeValidator,
    FnValidator,
    DatepickerAmPmPatchDirective,
    DynamicChildLoaderDirective,
    UniNavCardComponent,
    GridToolTipValueDialog,
    BreadcrumbComponent,
    InvoiceDialogComponent,
    ItemDetailLogDialogComponent,
    ItemDetailLogGridComponent,
    ItemStoryComponent,
    ScannerInputComponent,
    QbHealthBannerComponent,
    ProductExposurePanelComponent,
  ],
  declarations: [
    RepointColorDialogComponent,
    SizeMappingDialogComponent,
    AnalysisGridDialogComponent,
    EditPropComponent,
    BackButtonComponent,
    ButtonRendererComponent,
    MenuRendererComponent,
    BreezeValidatorDirective,
    OneFieldDialogComponent,
    EmailPromptDialogComponent,
    ValidationSaveErrorsDialogComponent,
    EditColComponent,
    EditNotesDialogComponent,
    FileSelectDialogComponent,
    FileViewDialogComponent,
    AddonFinderDialogComponent,
    AuthUserSelectDialogComponent,
    InfoComponent,
    AddressComponent,
    AgGridWrapperComponent,
    UniPageComponent,

    UniDialogComponent,
    UniFieldComponent,
    QbFieldComponent,
    UniDialogComponent,
    EditGenericEntityDialogComponent,
    // PdfComponent,
    AddressDialogComponent,
    HtmlEditorComponent,
    ImageInsertDialogComponent,
    KeyMenuDirective,
    AgLoadingOverlayComponent,
    TemplateRendererComponent,
    BreezeValidator,
    FnValidator,
    DatepickerAmPmPatchDirective,
    DynamicChildLoaderDirective,
    UniNavCardComponent,
    EntityStateRendererComponent,
    GridToolTipValueDialog,
    BreadcrumbComponent,
    InfoDialogComponent,
    YesNoDialogComponent,
    InvoiceDialogComponent,
    ItemDetailLogDialogComponent,
    ItemDetailLogGridComponent,
    ItemStoryComponent,
    ScannerInputComponent,
    QbHealthBannerComponent,
    ProductExposurePanelComponent,
  ],
  providers: [DialogService, ConfirmationService, MessageService],
})
export class SharedModule {
  constructor(http: HttpClient) {
    ModuleRegistry.registerModules([
      ClientSideRowModelModule,
      InfiniteRowModelModule,
      ServerSideRowModelModule,
      AgMenuModule,
      RangeSelectionModule,
      MasterDetailModule,
      SetFilterModule,
      RichSelectModule,
      RowGroupingModule,
      SideBarModule,
      ColumnsToolPanelModule,
      FiltersToolPanelModule,
      ExcelExportModule,
    ]);

    ModelLibraryBackingStoreAdapter.register();
    UriBuilderJsonAdapter.register();
    AjaxHttpClientAdapter.register(http);
    DataServiceWebApiAdapter.register();

    const emProvider = inject(UniformaxProvider);
    emProvider.prepare();
  }
}
